import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Fade,
  Stack,
  Typography,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';

import { IQuestion } from '../../../../store/QuestionSlice';
import {
  ISubmission,
  ISubmissionQuestion,
  getSubmissionState,
  updateSubmissionQuestionOpenAiFeedback,
} from '../../../../store/SubmissionSlice';
import { useAppSelector } from '../../../../hooks';
import { TShuffledData } from '..';

type TMarkingSchemeViewerProps = {
  questionData: IQuestion;
  shuffledData: TShuffledData;
  selectedQuestion: ISubmissionQuestion;
  submissionData: ISubmission;
};

const topBannerContainerStyle: React.CSSProperties = {
  border: '2px solid transparent',
  padding: '15px',
  color: '#12AAFF',
  background:
    'linear-gradient(90deg, rgba(219, 134, 248, 0.15) 0%, rgba(11, 196, 255, 0.15) 100%)',
  borderImage:
    'linear-gradient(90deg, rgb(219, 134, 248) 0%, rgb(18, 170, 255) 12.16%) 1',
};

const labelStyles: React.CSSProperties = {
  borderRadius: '5px',
  fontWeight: 600,
};

const DetailedMarkingSchemeBanner = () => {
  return (
    <Card
      style={{
        boxShadow: 'unset',
      }}
    >
      <CardContent style={{ padding: '0' }}>
        <div style={topBannerContainerStyle}>
          <Typography
            fontWeight={600}
            textAlign="center"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <img
              alt="star"
              src={`${process.env.PUBLIC_URL}/images/shapes/detailed-feedback-start.png`}
              style={{ marginRight: '10px', width: '28px', height: '24px' }}
            />
            Detailed Marking Scheme
          </Typography>
        </div>
      </CardContent>
    </Card>
  );
};

const MarkingSchemeViewer: React.FC<TMarkingSchemeViewerProps> = ({
  questionData,
  shuffledData,
  selectedQuestion,
  submissionData,
}) => {
  const dispatch = useDispatch();

  // Store
  const submissionState = useAppSelector(getSubmissionState);

  const { openAiCorrectAnswers, selectedOpenAiCorrectAnswer } = shuffledData;

  const handleFeedback = (feedback: 'like' | 'unlike') => {
    if (
      questionData &&
      submissionData &&
      submissionData.questions &&
      submissionData.questions.length
    ) {
      const submissionQuestionData = submissionData.questions.find(
        (submissionQuestion) => submissionQuestion.question === questionData._id
      );

      let openAiFeedback: -1 | 0 | 1 = 0;

      if (feedback === 'like') {
        openAiFeedback = 1;
      } else if (feedback === 'unlike') {
        openAiFeedback = -1;
      }

      if (
        submissionQuestionData?.openAiFeedback !== undefined &&
        openAiFeedback === submissionQuestionData.openAiFeedback
      ) {
        openAiFeedback = 0;
      }

      dispatch(
        updateSubmissionQuestionOpenAiFeedback({
          questionId: questionData._id,
          submissionId: submissionData._id,
          feedback: openAiFeedback,
        })
      );
    }
  };

  return (
    <Fade in>
      <div>
        {openAiCorrectAnswers.length ? (
          <Box style={{ marginBottom: '30px' }}>
            <Card style={{ borderRadius: '20px' }}>
              <CardContent style={{ padding: '30px 16px' }}>
                {selectedOpenAiCorrectAnswer ? (
                  <div style={{ marginBottom: '15px' }}>
                    <DetailedMarkingSchemeBanner />
                  </div>
                ) : null}
                {selectedOpenAiCorrectAnswer &&
                selectedOpenAiCorrectAnswer.isCorrect === false ? (
                  <div>
                    <div style={{ marginBottom: '15px' }}>
                      <Chip
                        size="small"
                        color="warning"
                        label="Why your answer is wrong"
                        style={{ ...labelStyles, backgroundColor: '#FFAB00' }}
                      />
                    </div>
                    {!(
                      selectedQuestion.isOpenEnded && questionData.openEnded
                    ) ? (
                      <div style={{ marginBottom: '15px' }}>
                        <Typography fontWeight={600}>
                          Answer: &nbsp;
                          {(selectedOpenAiCorrectAnswer.order + 1)
                            .toString()
                            .padStart(2, '0')}
                        </Typography>
                      </div>
                    ) : null}
                    {selectedOpenAiCorrectAnswer ? (
                      <>
                        <div style={{ marginBottom: '15px' }}>
                          <div
                            className="view ql-editor ql-viewer"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: (
                                selectedOpenAiCorrectAnswer.content || ''
                              ).trim(),
                            }}
                          />
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                          <div
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: (
                                selectedOpenAiCorrectAnswer.openaiResponse || ''
                              ).trim(),
                            }}
                          />
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : null}
                <div style={{ marginBottom: '15px' }}>
                  <Chip
                    size="small"
                    color="success"
                    style={labelStyles}
                    label={`Correct Answer${
                      openAiCorrectAnswers.length > 1 ? 's' : ''
                    }`}
                  />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  {openAiCorrectAnswers.map((item, index) => {
                    const orderNumber = (item.order + 1)
                      .toString()
                      .padStart(2, '0');

                    return (
                      <div key={index}>
                        {!(
                          selectedQuestion.isOpenEnded && questionData.openEnded
                        ) ? (
                          <div style={{ marginBottom: '15px' }}>
                            <Typography fontWeight={600}>
                              Answer: &nbsp;
                              {orderNumber}
                            </Typography>
                          </div>
                        ) : null}
                        <div style={{ marginBottom: '15px' }}>
                          <div
                            className="view ql-editor ql-viewer"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: (item.content || '').trim(),
                            }}
                          />
                        </div>
                        {selectedOpenAiCorrectAnswer &&
                          selectedOpenAiCorrectAnswer.isCorrect === true && (
                            <div style={{ marginBottom: '15px' }}>
                              <div
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                  __html: (
                                    selectedOpenAiCorrectAnswer.openaiResponse ||
                                    ''
                                  ).trim(),
                                }}
                              />
                            </div>
                          )}
                        {/* {!selectedOpenAiCorrectAnswer &&
                          openAiCorrectAnswers[0].openaiResponse && (
                            <div>
                              <div
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                  __html: (
                                    openAiCorrectAnswers[0].openaiResponse || ''
                                  ).trim(),
                                }}
                              />
                            </div>
                          )} */}
                      </div>
                    );
                  })}
                </div>
                <Stack direction="row">
                  <ButtonBase
                    color="info"
                    onClick={() => handleFeedback('like')}
                    disabled={submissionState.status === 'loading'}
                    sx={{
                      p: '5px',
                      borderRadius: '4px',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: 'info.main',
                      marginRight: '10px',
                      backgroundColor: 'light',
                    }}
                  >
                    {selectedQuestion.openAiFeedback === 1 ? (
                      <ThumbUpIcon color="info" />
                    ) : (
                      <ThumbUpOffAltIcon color="info" />
                    )}
                  </ButtonBase>
                  <ButtonBase
                    color="info"
                    disabled={submissionState.status === 'loading'}
                    onClick={() => handleFeedback('unlike')}
                    sx={{
                      p: '5px',
                      borderRadius: '4px',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: 'info.main',
                      backgroundColor: 'light',
                    }}
                  >
                    {selectedQuestion.openAiFeedback === -1 ? (
                      <ThumbDownIcon color="info" />
                    ) : (
                      <ThumbDownOffAltIcon color="info" />
                    )}
                  </ButtonBase>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        ) : null}
        <Box>
          <Card style={{ borderRadius: '20px' }}>
            <CardContent style={{ padding: '30px 16px' }}>
              <div style={{ marginBottom: '15px' }}>
                <Chip label="Marking Scheme" style={labelStyles} />
              </div>
              <div>
                <div
                  className="view ql-editor ql-viewer"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: questionData.markingSchema || '',
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </Box>
      </div>
    </Fade>
  );
};

export default MarkingSchemeViewer;
